
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import GridOnIcon from '@mui/icons-material/GridOn';
import HeightIcon from '@mui/icons-material/Height';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ViewListIcon from '@mui/icons-material/ViewList';
import { Box, Button, Checkbox, CircularProgress, Divider, IconButton, Input, LinearProgress, Paper, Popover, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue } from 'jotai';
import { clone } from 'lodash';
import * as React from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ADMIN_VALID_EMAIL, AI_GENERATION_PROGRESS, APP_ELEMENT_DIMENSIONS, KEY_CODES } from '../../constants';
import { activityAtom } from '../../stores/jotai/activities/activities';
import { DEFAULT_SELECTED_WEEK, baseAtom, checkRole, downloadExportCanvasSignedURL, getUIStateIsOptionSelected, isCanvasEmpty, params_getSlider, setCanvasPeriodValue, setCanvasSliderValue, setShowCanvasNotes, setTeamCanvasLayoutID, setUIStateIsOptionSelected, teamAtom } from '../../stores/jotai/base';
import { canvasActionRecieveLayout, canvasActionRecieveLibrary, canvasAtom } from '../../stores/jotai/canvas/canvas';
import { canvasDataAtom } from '../../stores/jotai/canvas/canvasData';

import { discoverySessionAtom } from '../../stores/jotai/discovery-session';
import { importCanvasAtom, setimportCanvasProgress } from '../../stores/jotai/generation-progress/import/importCanvasProcess';
import { hasPermissionCanvasWrite } from '../../stores/jotai/permissions';
import { useCreateCanvasLayout, useUpdateCanvasLayout } from '../../tanstackQuery/queries/canvas';
import { useDeleteCanvasBulkEntries } from '../../tanstackQuery/queries/canvas/useDeleteCanvasBulkEntries';
import { useExportCanvas, useImportCanvas } from '../../tanstackQuery/queries/canvas/useImportExportCanvas';
import { useUpdateCanvasDescription } from '../../tanstackQuery/queries/canvas/useUpdateCanvasDescription';
import * as uuid from '../../util/uuid';
import AlertDialog from '../app/alert-dialog';
import IwButton from '../app/IwButton';
import IwMenu from '../app/IwMenu';
import IwSwitch from '../app/IwSwitch';
import MaterialPrompt from '../app/material-prompt';
import { DropdownV2 } from '../assorted/dropdown-v2';
import ManagedSlider from '../generics/components/managed-slider';
import { PeriodSelector } from '../selectors/period-selector';
import MaterialCanvasNotesEditor from './material-canvas-notes-editor';
import { canvasViewOptionsAtom } from '../../stores/jotai/canvas/canvasViewOptions';
import { canvasFilterOptionsAtom } from '../../stores/jotai/canvas/canvasFilterOptions';
import { renderErrorToast } from '../../../lib/ErrorToastMessage';

export default function MaterialCanvasToolbar(props: any) {
    const [canvasState, setCanvasState] = useAtom(canvasAtom);
    const [canvasData,setCanvasData]=useAtom(canvasDataAtom);
    const theme = useTheme();
    const [canvasReader] = useAtom(
        React.useMemo(
            () => atom((get) => get(canvasAtom)),
            []
        )
    )
    const iwMenuRef = useRef();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isDeletingCanvasEntires, setIsDeletingCanvasEntires] = useState(false);
    const [isEnableCanvasDownload,setIsEnableCanvasDownload]= useState(false)
    const [vcOpen, setVCOpen] = React.useState(false);
    const [promptUser, setPromptUser] = React.useState(false);
    const [promptUserRename, setPromptUserRename] = React.useState(false);
    const [base, setBase] = useAtom(baseAtom);
    const discoverySession = useAtomValue(discoverySessionAtom);
    const [editableDescription, setEditableDescription] = React.useState(canvasData.description);
    const [isEditing, setIsEditing] = React.useState(false);
    const [canvasViewOptions, setCanvasViewOptions] = useAtom(canvasViewOptionsAtom);
    const [canvasFilterOptions, setCanvasFilterOptions] = useAtom(canvasFilterOptionsAtom);
    const activities = useAtomValue(activityAtom);
    const [importCanvasState,setImportCanvasState]=useAtom(importCanvasAtom);
    const team = useAtomValue(teamAtom);
    const {
        showSkinnyCanvas,
        showCommentCount,
        showLinkedInsightsCount,
        showLinkedHypothesesCount,
        showHypothesesIndication
    } = canvasViewOptions;
    const {
        showValidatedHypotheses,
        showInvalidatedHypotheses,
        showUnresolvedHypotheses,
    } = canvasFilterOptions;

    const { keycloak } = useKeycloak()
    const client = useQueryClient();
    const {
      data: exportCanvasData,
      isLoading: isExportCanvasLoading,
      refetch,
    } = useExportCanvas(
      { accessToken: keycloak?.token },
      { groupID: team?.groupID },
      { enabled: isEnableCanvasDownload }
    );
    const { mutateAsync: createCanvasLayout } = useCreateCanvasLayout(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["canvas"] });
          client.invalidateQueries({ queryKey: ["canvas_layout"] });
        }
      }
    );
    const { mutateAsync: updateCanvasLayout } = useUpdateCanvasLayout(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["canvas"] });
          client.invalidateQueries({ queryKey: ["canvas_layout"] });
        }
      }
    );

    const { mutateAsync: updateCanvasDescription } = useUpdateCanvasDescription(
        { accessToken: keycloak?.token },
        {
          onSuccess: () => {
            client.invalidateQueries({ queryKey: ["canvas"] });
          }
        }
      );
 
    const { mutateAsync: handleDeleteCanvasEntries } =
      useDeleteCanvasBulkEntries(
        { accessToken: keycloak?.token },
        {},
        {
          onSuccess: () => {
            client.invalidateQueries({ queryKey: ["canvas"] });
          },
        }
      );
    const { mutateAsync: handleImportCanvas } = useImportCanvas(
      { accessToken: keycloak?.token },
      {
        groupID: team?.groupID,
        canvasID: team?.groupID,
        sessionID: discoverySession?.sessionID,
      },
      {
        onSuccess: (res) => {
          client.invalidateQueries({ queryKey: ["canvas"] });
          if (!!res?.generationStarted) {
            setimportCanvasProgress(
              setImportCanvasState,
              team?.groupID,
              AI_GENERATION_PROGRESS.IN_PROGRESS
            );
          } else {
            setimportCanvasProgress(
              setImportCanvasState,
              team?.groupID,
              AI_GENERATION_PROGRESS.NOT_STARTED
            );
          }
        },
      }
    );
  
    const handleFileUpload = async () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
        fileInputRef.current.click(); 
    
        fileInputRef.current.onchange = async (event) => {
          const file = event.target.files?.[0];
          if (file) {
            const formData = new FormData();
            formData.append("file", file); 
    
            try {
               await handleImportCanvas(formData);
               iwMenuRef.current?.closeMenu();
            } catch (error) {
              renderErrorToast("Something went wrong! Try again")
            }
          }
          fileInputRef.current.value = "";
        };
      }
    };

    const handleDeleteCanvas = () => {
      setIsDeletingCanvasEntires(true);
      const handleDelete = handleDeleteCanvasEntries;
      handleDelete({
        canvasID: canvasData?.canvasID,
        teamID: team?.groupID,
      })
        .then(async () => {
          setShowCanvasNotes(setBase, false);
          setCanvasSliderValue(setBase, activities?.length);
          setShowConfirmationModal(false);
          await handleFileUpload();
        })
        .catch((error) => {
          renderErrorToast(error);
        })
        .finally(() => {
          setIsDeletingCanvasEntires(false);
          setShowConfirmationModal(false);
        });
    };
    const importInterview = async () => {
      if (!isCanvasEmpty(canvasData)) {
        iwMenuRef.current?.closeMenu();
        setShowConfirmationModal(true);
      } else {
        await handleFileUpload();
      }
    };
      const exportInterview = async () => {
        if (isExportCanvasLoading) {
          // Prevent duplicate or overlapping downloads
          return;
        }
        setIsEnableCanvasDownload(true);
        try {
          const { data: exportCanvasData, isSuccess } = await refetch();
          if (isSuccess) {
            downloadExportCanvasSignedURL(
              exportCanvasData.signedURL,
              team?.groupID
            );
          } else {
            renderErrorToast("Export failed: try again later")
          }
        } catch (error) {
          renderErrorToast("Export failed: try again later")
        } finally {
          setIsEnableCanvasDownload(false);
        }
      }; 

    

    const setShowSkinnyCanvas = (state) => {
        setCanvasViewOptions((options) => ({
            ...options,
            showSkinnyCanvas: state,
        }));
    };

    const setShowCommentCount = (state) => {
        setCanvasViewOptions((options) => ({
            ...options,
            showCommentCount: state,
        }));
    };

    const setShowLinkedInsightsCount = (state) => {
        setCanvasViewOptions((options) => ({
            ...options,
            showLinkedInsightsCount: state,
        }));
    };

    const setShowLinkedHypothesesCount = (state) => {
        setCanvasViewOptions((options) => ({
            ...options,
            showLinkedHypothesesCount: state,
        }));
    };

    const setShowValidatedHypotheses = (state) => {
        setCanvasFilterOptions((options) => ({
            ...options,
            showValidatedHypotheses: state,
        }));
    };

    const setShowInvalidatedHypotheses = (state) => {
        setCanvasFilterOptions((options) => ({
            ...options,
            showInvalidatedHypotheses: state,
        }));
    };

    const setShowUnresolvedHypotheses = (state) => {
        setCanvasFilterOptions((options) => ({
            ...options,
            showUnresolvedHypotheses: state,
        }));
    };

    const setShowHypothesesIndication = (state) => {
        setCanvasViewOptions((options) => ({
            ...options,
            showHypothesesIndication: state,
        }));
    };

    const handleSavingDescription = async () => {

       await updateCanvasDescription({
         canvasID: canvasData?.canvasID,
         description: editableDescription,
         sessionID: discoverySession?.sessionID
       });
    }

    const [canvasLayoutEditorPopoverOpen, setCanvasLayoutEditorPopoverOpen] = useState(false);
    const notesPRef = useRef(null);
    const canvasLayoutEditorRef = useRef(null);

    // save the code in this component from refactoring and keep it pure to the prop existing
    if (props && canvasState?.library === undefined) {
        return <span />
    }

    const renderCanvasControls = () => {
        let additionalControlsSection = [];
        return additionalControlsSection;
    }

    const renderWeeklyDropdown = () => {
        const {
            cohort,
            sliderDateLabel
        } = props;

        const dropdownWrapperStyle = {
            display: 'inline-block',
            lineHeight: '15px',
            marginRight: "6px",
        }

        const {
            canvasSliderMax,
        } = params_getSlider(base,activities);

        return <span id="joy-canvas-period-selector" className="period-selector-wrapper" style={dropdownWrapperStyle}>
            <PeriodSelector
                cohort={cohort}
                isForCanvas={true}
                selectedWeek={
                    canvasData?.selectedWeek
                        ? canvasData?.selectedWeek?.toString()
                        : DEFAULT_SELECTED_WEEK?.toString()
                }
                canvasDateLabel={sliderDateLabel}
                onWeekChange={(week: string) => {
                    setCanvasData(prev => ({...prev, selectedWeek: Number(week)}));
                    setCanvasSliderValue(setBase, canvasSliderMax);
                }}
                theme={theme}
            />
        </span>
    }

    let renderSubToolbar = () => {
        const {
            toggleHideStaleEntries,
            alternativeCanvasType,
            appState,
            isPPTDownloading,
            downloadAsPPT,
            user,
            cohort
        } = props;

        let isSystemAdmin = checkRole(base, 'isSystemAdministrator') && ADMIN_VALID_EMAIL.includes(base.user.email);

        let isVV = !getUIStateIsOptionSelected(base, 'canvas_filter_showValidated');
        const menuItemLoader = (
             <CircularProgress size={16} sx={{ marginLeft: "auto" }} />
        );
        const menuItem = [
          {
            id: "import-canvas",
            label: "Import Canvas (JSON)",
            tooltipProps: {
              title: "import Canvas",
            },
            suffix:
              importCanvasState[team?.groupID]?.importCanvasInProgress ===
              AI_GENERATION_PROGRESS?.IN_PROGRESS ? (
                menuItemLoader
              ) : (
                <FileUploadOutlinedIcon />
              ),
            onClick: () => {
              importInterview();
            },
          },
          {
            id: "export-canvas",
            label: "Export for Data Transfer (JSON)",
            tooltipProps: {
              title: "Export Canvas",
            },
            suffix: isExportCanvasLoading ? (
              menuItemLoader
            ) : (
              <FileDownloadOutlinedIcon />
            ),
            onClick: () => {
              exportInterview();
            },
            disabled: isCanvasEmpty(canvasData),
          },
          {
            id: "toggle_orientation",
            label: "Skinny Canvas",
            suffix: <IwSwitch checked={showSkinnyCanvas} size="small" />,
            onClick: () => {
              setShowSkinnyCanvas(!showSkinnyCanvas);
            },
          },
          {
            id: "comment_view",
            label: "Show Comment Count",
            suffix: <IwSwitch checked={showCommentCount} size="small" />,
            onClick: () => {
              setShowCommentCount(!showCommentCount);
            },
          },
          {
            id: "insight_view",
            label: "Show Linked Insights Count",
            suffix: <IwSwitch checked={showLinkedInsightsCount} size="small" />,
            onClick: () => {
              setShowLinkedInsightsCount(!showLinkedInsightsCount);
            },
          },
          {
            id: "hypo_view",
            label: "Show Linked Hypotheses Count",
            suffix: (
              <IwSwitch checked={showLinkedHypothesesCount} size="small" />
            ),
            onClick: () => {
              setShowLinkedHypothesesCount(!showLinkedHypothesesCount);
            },
          },
        ];
        if (base.team?.isAiCanvasGenerationEnabled) {
          menuItem.push({
            id: "indication",
            label: "Show Created By (AI, Human)",
            suffix: <IwSwitch checked={showHypothesesIndication} size="small" />,
            onClick: () => {
              setShowHypothesesIndication(!showHypothesesIndication);
            },
          });
        }

        return (
          <div
            id="joy-canvas-action-icons"
            style={{
              display: "flex",
              marginLeft: 0,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isSystemAdmin ? (
              <div style={{}} ref={canvasLayoutEditorRef}>
                <Tooltip title="Canvas Layout Options">
                  <IconButton
                    onClick={() => {
                      setCanvasLayoutEditorPopoverOpen(true);
                    }}
                  >
                    <GridOnIcon />
                  </IconButton>
                </Tooltip>
              </div>
            ) : undefined}

            {base.team?.isAiCanvasGenerationEnabled ? (
              <Tooltip title="Generate Business model with AI">
                <IconButton
                  sx={{ margin: "0 4px" }}
                  onClick={() => {
                    setShowCanvasNotes(setBase, true);
                  }}
                >
                  <AutoFixHighIcon ref={notesPRef} />
                </IconButton>
              </Tooltip>
            ) : null}

            <IwMenu
              label={<FilterListOutlinedIcon />}
              tooltipProps={{
                title: "Canvas Filters",
              }}
              triggerComponentSx={{
                margin: "0 8px",
              }}
              items={[
                {
                  id: "validated",
                  label: "Validated hypotheses",
                  suffix: (
                    <IwSwitch checked={showValidatedHypotheses} size="small" />
                  ),
                  onClick: () => {
                    setShowValidatedHypotheses(!showValidatedHypotheses);
                  },
                },
                {
                  id: "invalidated",
                  label: "Invalidated hypotheses",
                  suffix: (
                    <IwSwitch
                      checked={showInvalidatedHypotheses}
                      size="small"
                    />
                  ),
                  onClick: () => {
                    setShowInvalidatedHypotheses(!showInvalidatedHypotheses);
                  },
                },
                {
                  id: "unresolved",
                  label: "Unresolved hypotheses",
                  suffix: (
                    <IwSwitch checked={showUnresolvedHypotheses} size="small" />
                  ),
                  onClick: () => {
                    setShowUnresolvedHypotheses(!showUnresolvedHypotheses);
                  },
                },
              ]}
            />
            {isPPTDownloading ? (
              <CircularProgress
                size={36}
                thickness={5}
                sx={{ padding: "8px" }}
              />
            ) : ( 
              <IwMenu
                ref={iwMenuRef}
                label={<SettingsOutlinedIcon />}
                tooltipProps={{
                  title: "Canvas Options",
                }}
                triggerComponentSx={{
                  margin: "0 8px",
                  padding: "2px 0",
                }}
                items={menuItem}
              />
            )}
          </div>
        );
    }

    const updateCanvasLibraryOptions = (accessor, newValue, segment) => {

        let newLibrary = clone(canvasState?.library);

        for (let i = 0; i < newLibrary?.length; i++) {
            if (newLibrary[i]?.canvasLayoutID === canvasState?.layout) {
                for (let s = 0; s < newLibrary[i]?.layout?.layout?.length; s++) {
                    if (newLibrary[i]?.layout?.layout[s]?.i === segment) {
                        newLibrary[i].layout.layout[s][accessor] = newValue;
                    }
                }
            }
        }

        canvasActionRecieveLibrary(setCanvasState, newLibrary);
    }

    const canvasLibraryOptions = () => {
        const {
            library
        } = canvasState;

        let buffer = [];

        for (let i = 0; i < library?.length; i++) {
            buffer.push({
                key: library[i]?.canvasLayoutID,
                value: library[i]?.label
            })
        }

        return buffer;
    }

    const renderCustomCanvasTextualInput = (defaultValue, accessor, segment) => {
        return <Input sx={{ width: '250px' }} defaultValue={defaultValue} onChange={(e) => {
            const _value = e.target.value;
            const _accessor = accessor;

            try {
                updateCanvasLibraryOptions(_accessor, _value, segment);
            }
            catch (e) {

            }
        }} />
    }

    const renderCustomCanvasNumericalInput = (defaultValue, accessor, segment) => {
        return <Input sx={{ width: '50px' }} value={defaultValue} onChange={(e) => {
            const _value = e.target.value;
            const _accessor = accessor;

            try {
                updateCanvasLibraryOptions(_accessor, parseFloat(_value), segment);
            }
            catch (e) {

            }
        }} />
    }

    const getCurrentLayoutPack = () => {
        const layoutID = canvasReader?.layout;

        let buffer = undefined;

        for (let i = 0; i < canvasState?.library?.length; i++) {
            if (canvasState?.library[i]?.canvasLayoutID === layoutID) {
                buffer = canvasState?.library[i];
            }
        }

        return buffer;
    }

    const getCurrentLayoutName = () => {
        const layoutID = canvasReader?.layout;

        let buffer = "";

        for (let i = 0; i < canvasState?.library?.length; i++) {
            if (canvasState?.library[i]?.canvasLayoutID === layoutID) {
                buffer = canvasState?.library[i]?.label;
            }
        }

        return buffer;
    }

    const getCurrentLayout = () => {
        const layoutID = canvasState?.layout;

        let buffer: any = [];

        for (let i = 0; i < canvasState?.library?.length; i++) {
            if (canvasState?.library[i]?.canvasLayoutID === layoutID) {
                buffer = canvasState?.library[i]?.layout?.layout;
            }
        }

        return buffer;
    }

    const renderDialogs = () => {
        const columns = [
            {
                Header: 'Label',
                accessor: 'label',
                width: 90,
                resizable: false,
            }
        ];

        let currentLayout = getCurrentLayout();

        let rows = [];

        for (let i = 0; i < currentLayout?.length; i++) {
            const row = currentLayout[i];

            rows.push(<TableRow
                key={row.i}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
                <TableCell sx={{ margin: '0px', padding: '0px' }} align="left" component="th" scope="row">
                    <div style={{ paddingLeft: '15px' }}>{renderCustomCanvasTextualInput(row.label, 'label', row.i!)}</div>
                </TableCell>
                <TableCell sx={{ margin: '0px', padding: '0px' }} align="right">{renderCustomCanvasNumericalInput(row.x, 'x', row.i!)}</TableCell>
                <TableCell sx={{ margin: '0px', padding: '0px' }} align="right">{renderCustomCanvasNumericalInput(row.y, 'y', row.i!)}</TableCell>
                <TableCell sx={{ margin: '0px', padding: '0px' }} align="right">{renderCustomCanvasNumericalInput(row.w, 'w', row.i!)}</TableCell>
                <TableCell sx={{ margin: '0px', padding: '0px' }} align="right">{renderCustomCanvasNumericalInput(row.h, 'h', row.i!)}</TableCell>
                <TableCell sx={{ margin: '0px', padding: '0px' }} align="right">
                    <Checkbox onChange={(e, checked) => {
                        const newValue = checked ? 1 : 0;
                        updateCanvasLibraryOptions('favorite', newValue, row.i!);
                    }} checked={row.favorite === 1 ? true : false} inputProps={{ 'aria-label': 'controlled' }} />
                </TableCell>
            </TableRow>);
        }

        let table = <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer key={canvasState?.layout} sx={{ maxHeight: '350px' }}>
                <Table aria-label="custom canvas options table" stickyHeader={true}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Segment</TableCell>
                            <TableCell align="right"><ArrowBackIcon /></TableCell>
                            <TableCell align="right"><ArrowDownwardIcon /></TableCell>
                            <TableCell align="right"><CompareArrowsIcon /></TableCell>
                            <TableCell align="right"><HeightIcon /></TableCell>
                            <TableCell align="right"><ViewListIcon /></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>

        const renderVisibilityControls = () => {

            let ddItems = [
                {
                    key: 'public', value: 'public'
                },
                {
                    key: 'private', value: 'private'
                }
            ];

            const pack = getCurrentLayoutPack();

            let scope = undefined;

            try {
                scope = pack!.scope;
            }
            catch (e) {

            }

            return <><Popover
                id={"visibility-controls"}
                open={vcOpen}
                anchorEl={canvasLayoutEditorRef!.current}
                onClose={() => {
                    setVCOpen(false);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ width: '600px', padding: '5px', height: '400px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <DropdownV2
                            items={ddItems}
                            selected={scope}
                            onChange={(event, selected, check) => {
                                //canvasActionRecieveLayout(setCanvasState, selected);
                            }}
                            theme={theme}
                        />
                        <Button onClick={() => { setVCOpen(false); }}>done</Button> <Button onClick={() => { setVCOpen(false); }}>cancel</Button>
                    </div>
                </Box>
            </Popover>
                <PublicIcon onClick={() => {
                    setVCOpen(true);
                }} sx={{ marginLeft: '19px', marginRight: '3px' }} />
            </>
        }


        return <Popover
            id={"id"}
            open={canvasLayoutEditorPopoverOpen}
            anchorEl={canvasLayoutEditorRef!.current}
            onClose={() => {
                setCanvasLayoutEditorPopoverOpen(false);
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
        >
            <Box sx={{ width: '600px', padding: '5px', height: '400px' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <div style={{ flex: '1', width: '100%', flexDirection: 'column' }}>
                        <div style={{ flex: '0', maxWidth: '250px', width: '250px', float: 'left' }}>
                            <DropdownV2
                                items={canvasLibraryOptions()}
                                selected={canvasState?.layout}
                                onChange={(event, selected, check) => {
                                    canvasActionRecieveLayout(setCanvasState, selected);
                                }}
                                theme={theme}
                            />
                        </div>
                        <div style={{ float: 'left', marginLeft: '5px', marginTop: '7px' }}>
                            <EditIcon onClick={() => {
                                setPromptUserRename(true);
                            }} />
                            <ContentCopyIcon onClick={() => {
                                // copy the current and create a new one
                                setPromptUser(true);
                            }} />
                        </div>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flex: '1',
                                float: 'right'
                            }}
                        >
                            {renderVisibilityControls()}
                            <Divider orientation="vertical" flexItem />
                            <Button disabled={true}
                                sx={{
                                    bgcolor: '#1c445b',
                                    '&:hover': {
                                        backgroundColor: '#3683b0',
                                    },
                                    marginLeft: '4px'
                                }}
                                onClick={() => { }}
                                variant="contained">
                                Share
                            </Button>
                            <Button disabled={true}
                                sx={{
                                    bgcolor: '#1c445b',
                                    '&:hover': {
                                        backgroundColor: '#3683b0',
                                    },
                                    marginLeft: '4px'
                                }}
                                onClick={() => { }}
                                variant="contained">
                                Cancel
                            </Button>
                            <Divider sx={{ marginLeft: '3px', marginRight: '3px' }} orientation="vertical" flexItem />
                            <Button disabled={false}
                                sx={{
                                    bgcolor: '#1c445b',
                                    '&:hover': {
                                        backgroundColor: '#3683b0',
                                    }
                                }}
                                onClick={() => {
                                    const layoutID = canvasState?.layout;
                                    const pack = getCurrentLayoutPack();

                                    updateCanvasLayout({ layoutID, sessionID: discoverySession?.sessionID, ...pack }).then(() => {
                                        setTeamCanvasLayoutID(setBase, layoutID);
                                    })

                                    // axios.put(`${process.env.NEXT_PUBLIC_LINX_URL}/canvas_layouts`, {
                                    //     layoutID,
                                    //     ...pack
                                    // }).then((res) => {
                                    //     setTeamCanvasLayoutID(setBase, layoutID);
                                    // });
                                }}
                                variant="contained">
                                Save
                            </Button>
                        </Box>
                    </div>
                    <div style={{ flex: '1', width: '100%', height: '100%' }}>
                        {table}
                    </div>
                </div>
            </Box>
        </Popover>
    }

    const isArchiveModeAvailableAndEnabled = React.useCallback(() => {
        const { isMax } = params_getSlider(base,activities);

        if (canvasData.selectedWeek !== DEFAULT_SELECTED_WEEK) {
            return true;
        }

        return !isMax;
    }, [base?.canvasSliderValue, activities, canvasData?.selectedWeek]);

    const handleLockIconClick = () => {
        const { canvasSliderMax } = params_getSlider(base,activities);

        setCanvasSliderValue(setBase, canvasSliderMax);
        setCanvasPeriodValue(setCanvasData, 0);
    };

    const renderLockIcon = () => {
        return isArchiveModeAvailableAndEnabled() ? (
            <Tooltip title="Click to unlock canvas">
                <IconButton
                    color="primary"
                    sx={{
                        padding: "2px",
                        marginRight: "4px",
                    }}
                    onClick={() => {
                        handleLockIconClick();
                    }}
                    onTouchStart={() => {
                        handleLockIconClick();
                    }}
                    aria-label="team stats"
                    component="label"
                >
                    <LockIcon sx={{ fontSize: "20px" }} />
                </IconButton>
            </Tooltip>
        ) : null;
    }

    let renderWeeklySlider = () => {
        const {
            canvasSliderValue,
            canvasSliderMax,
        } = params_getSlider(base,activities);

        return canvasSliderMax > 1 && Object.keys(props?.entries)?.length > 0 ? (
            <Stack
                direction="row"
                spacing={0}
                sx={{
                    margin: "0 12px 0 0",
                }}
            >
                <ManagedSlider
                    max={canvasSliderMax}
                    value={canvasSliderValue}
                    sx={{
                        width: "200px",
                    }}
                    onChange={(e) => {
                        //@ts-ignore
                        setCanvasSliderValue(setBase, e);
                        setCanvasPeriodValue(setCanvasData, 0);
                    }}
                />
            </Stack>
        ) : null;
    }

    const handleCanvasDesctionSave = () => {
        setIsEditing(false);
        handleSavingDescription();
    }

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_filter_showValidated",
            showValidatedHypotheses
        );
    }, [showValidatedHypotheses]);

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_filter_showInvalidated",
            showInvalidatedHypotheses
        );
    }, [showInvalidatedHypotheses]);

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_filter_showUnresolved",
            showUnresolvedHypotheses
        );
    }, [showUnresolvedHypotheses]);

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_orientation",
            showSkinnyCanvas,
        );
    }, [showSkinnyCanvas]);

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_comment_view",
            showCommentCount,
        );
    }, [showCommentCount]);

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_insight_view",
            showLinkedInsightsCount,
        );
    }, [showLinkedInsightsCount]);

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_linked_view",
            showLinkedHypothesesCount,
        );
    }, [showLinkedHypothesesCount]);

    React.useEffect(() => {
        setUIStateIsOptionSelected(
            setBase,
            base,
            "canvas_indication_view",
            showHypothesesIndication,
        );
    }, [showHypothesesIndication]);

    if (canvasState.library === undefined) return <LinearProgress className='iw-progress' />

    return (
      <Box
        id="joy-canvas-toolbar"
        sx={{
          backgroundColor: "rgb(250,250,250)",
          borderBottom: `1px solid ${theme?.custom?.borderGrey}`,
          height: APP_ELEMENT_DIMENSIONS.CANVAS_TOOLBAR.HEIGHT,
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }} 
        />
        <AlertDialog
          title="Delete All Hypotheses?"
          description="Confirming this action will delete all of the hypotheses, comments, links and histories in your canvas. This action is not reversible. Are you sure you want to continue?"
          open={showConfirmationModal}
          onClose={() => {
            setShowConfirmationModal(false);
          }}
          confirmButton={
            <IwButton
              variant="contained"
              onClick={() => handleDeleteCanvas()}
              loading={isDeletingCanvasEntires}
              disabled={isDeletingCanvasEntires}
            >
              Confirm Deletion
            </IwButton>
          }
          abortButton={
            <IwButton
              variant="outlined"
              onClick={() => {
                setShowConfirmationModal(false);
              }}
            >
              Cancel
            </IwButton>
          }
        />
        {renderDialogs()}
        <MaterialPrompt
          prompt={"What would you like the duplicate to be called?"}
          open={promptUser}
          close={() => {
            setPromptUser(false);
          }}
          done={(value) => {
            const canvasLayoutID = uuid.generate();
            const pack = {
              ...getCurrentLayoutPack(),
              label: value,
            };

            createCanvasLayout({
              canvasLayoutID,
              sessionID: discoverySession?.sessionID,
              ...pack,
            }).then(() => {
              let newLibrary = clone(canvasState?.library);

              newLibrary.push({
                ...pack,
                canvasLayoutID: canvasLayoutID,
              });

              canvasActionRecieveLibrary(setCanvasState, newLibrary);
            });

            setPromptUser(false);
          }}
        />
        {promptUserRename ? (
          <MaterialPrompt
            prompt={"What would you like the rename the canvas to?"}
            defaultValue={getCurrentLayoutName()}
            open={promptUserRename}
            close={() => {
              setPromptUserRename(false);
            }}
            done={(value) => {
              let newLibrary = clone(canvasState?.library);

              for (let i = 0; i < newLibrary?.length; i++) {
                if (newLibrary[i]?.canvasLayoutID === canvasState?.layout) {
                  newLibrary[i].label = value;
                }
              }

              canvasActionRecieveLibrary(setCanvasState, newLibrary);

              setPromptUserRename(false);
            }}
          />
        ) : undefined}
        <Toolbar disableGutters variant="dense" sx={{ padding: "0 5px" }}>
          <Box id="joy-canvas-title" sx={{ flexGrow: 1, paddingRight: "10px",width:"50%" }}>
            {/* work on this */}
            <MaterialCanvasNotesEditor canvasData={canvasData}/>
            <Stack direction="row" spacing={0} >
              {isEditing ? (
                <TextField
                  className="iw-component-input-minimal font-weight-normal no-padding canvas-desc-box"
                  value={editableDescription}
                  sx={{
                    height: "40px",
                    padding: "0px",
                    marginLeft: "10px",
                    width: "100%",
                    "& .MuiInputBase-input": {
                      padding: "8px",
                    },
                  }}
                  autoFocus={true}
                  placeholder=""
                  disabled={!hasPermissionCanvasWrite(base)}
                  variant="outlined"
                  onBlur={(e) => {
                    // save it
                    handleCanvasDesctionSave();
                  }}
                  onChange={(e) => {
                    setEditableDescription(e.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === KEY_CODES.ENTER) {
                      handleCanvasDesctionSave();
                    }
                  }}
                />
              ) : (
                <Button
                  variant="text"
                  sx={{
                    paddingBottom: "5px",
                    color: "rgba(0,0,0,0.6)",
                    ".MuiButton-startIcon": { marginLeft: "-1px" },
                  }}
                  onClick={() => {
                    setIsEditing(true);
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: "left",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      textTransform: "none",
                    }}
                  >
                    {editableDescription
                      ? editableDescription
                      : "Enter a canvas description"}
                  </Typography>
                </Button>
              )}
            </Stack>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ color: "rgba(0, 0, 0, 0.6)" }}
          >
            {renderLockIcon()}
            {renderCanvasControls()}
            {canvasData.selectedWeek === DEFAULT_SELECTED_WEEK
              ? renderWeeklySlider()
              : null}
            {renderWeeklyDropdown()}
            {renderSubToolbar()}
          </Stack>
        </Toolbar>
      </Box>
    );
}
